import type {ReactElement} from 'react';
import type {QRSocialInput} from '@Libraries/qr-code/qr-code.types';
import {QRPayloadField} from '@Libraries/qr-code/qr-code.types';
import {QRField} from '@Components/qr-code-generator/components/qr-field/qr-field';
import {isWhatsAppChatNumber} from '@Libraries/qr-code/qr-payload.library';
import {getQRSocialPlatformFromTypedLink, QRSocialPlatform, QR_SOCIAL_PLATFORM_NAMES} from '@Libraries/qr-code/qr-social-platform.library';
import type {QRFormProps} from '@Components/qr-code-generator/qr-code-generator.types';

export function QRSocialForm({input, issues, onPatch}: QRFormProps<QRSocialInput>): ReactElement {
  /** A bare phone number is a WhatsApp chat, which only the payload builder would otherwise know — the typed field has no host to read. */
  const getTypedPlatform = (): QRSocialPlatform | null => {
    return isWhatsAppChatNumber(input.link) ? QRSocialPlatform.WHATSAPP : getQRSocialPlatformFromTypedLink(input.link);
  };

  const getLinkHelpText = (): string => {
    const platform = getTypedPlatform();
    if (platform === null) {
      return window.i18next.t('pmwjs_qr_generator_social_link_help_generic');
    }
    return window.i18next.t('pmwjs_qr_generator_social_link_help', {platform: QR_SOCIAL_PLATFORM_NAMES[platform]});
  };

  const renderWhatsAppMessageField = (): ReactElement | null => {
    if (!isWhatsAppChatNumber(input.link)) {
      return null;
    }
    return (
      <QRField
        label={window.i18next.t('pmwjs_qr_generator_whatsapp_message_label')}
        placeholder={window.i18next.t('pmwjs_qr_generator_whatsapp_message_placeholder')}
        value={input.whatsAppMessage}
        isMultiline
        onValueChange={(whatsAppMessage): void => {
          onPatch({whatsAppMessage});
        }}
      />
    );
  };

  return (
    <>
      <QRField
        label={window.i18next.t('pmwjs_qr_generator_social_link_label')}
        placeholder={window.i18next.t('pmwjs_qr_generator_social_link_placeholder')}
        helpText={getLinkHelpText()}
        value={input.link}
        field={QRPayloadField.LINK}
        issues={issues}
        onValueChange={(link): void => {
          onPatch({link});
        }}
      />
      {renderWhatsAppMessageField()}
    </>
  );
}
